// web/src/hooks/useDateRangeManager.ts - helps with managing startDate, endDate, and date manipulations

import { useState, useEffect, useCallback, useMemo } from 'react';
import { CustomerInfo } from '../models/CustomerData';
import { TimeSliceViewType, TimeSliceWindow } from '@/models/TimeSlice';
import { TimeSliceGenerator } from '../utils/TimeSliceGenerator'

const getDefaultMinDateString = () => '2025-04-01'; // Default earliest selectable date (adjust as needed)
const getTodayString = () => new Date().toISOString().split('T')[0];

const getDateInThePast = (numDaysInPast: number, fromDate: Date = new Date()): Date => {
  const date = new Date(fromDate);
  date.setDate(date.getDate() - numDaysInPast);
  return date;
};

const getDateInThePastHours = (numHoursInPast: number, fromDate: Date = new Date()): Date => {
    const date = new Date(fromDate);
    date.setHours(date.getHours() - numHoursInPast);
    return date;
};

interface UseDateRangeManagerProps {
  selectedCustomer: CustomerInfo | null;
}

const DEFAULT_TIMESLICE_VIEWTYPE = '2h';

export const useDateRangeManager = ({ selectedCustomer }: UseDateRangeManagerProps) => {
    
  const [pulseView, setPulseView] = useState<TimeSliceViewType>(DEFAULT_TIMESLICE_VIEWTYPE);
  const timeSliceGenerator = useMemo(() => new TimeSliceGenerator(), []);
  const [timeSliceWindow, setTimeSliceWindow] = useState<TimeSliceWindow>(() =>
    timeSliceGenerator.generateTimeSlice(DEFAULT_TIMESLICE_VIEWTYPE, new Date(), true)
  );

  const generateSlice = useCallback((
    view: TimeSliceViewType,
    minDate?: string,
    maxDate?: string,
    customDate?: Date
  ) => {

    const data = timeSliceGenerator.generateTimeSlice(view, customDate, true);

    if (view === '7d') {
      const dateNow = new Date()

      const inputMaxDate = maxDate ? new Date( maxDate.replace(/-/g, '/')) : dateNow;
      if (data.endTime > inputMaxDate) { 
        data.endTime = inputMaxDate;
        data.endTimeStr = inputMaxDate.toISOString();
      }

      const inputMinDate = minDate ? new Date( minDate.replace(/-/g, '/')) : dateNow;
      if (data.startTime < inputMinDate) { 
        data.startTime = inputMinDate;
        data.startTimeStr = inputMinDate.toISOString();
      }
    }

    setTimeSliceWindow(data);
    console.log(`Generated Time Slice for ${view} (Rounded) is: ${data}`);
  }, [timeSliceGenerator]);

  // Effect to generate initial slice on mount
  useEffect(() => {
    generateSlice(pulseView, selectedCustomer?.minDateAllowed, selectedCustomer?.maxDateAllowed);
  }, [pulseView, selectedCustomer, generateSlice]);

  const handleEndDateChange = (newEndDate: Date) => {

    console.log( `New End Date is given: ${newEndDate}`);
    generateSlice(pulseView, selectedCustomer?.minDateAllowed, selectedCustomer?.maxDateAllowed, newEndDate);
  };

  const handlePulseViewChange = (newView: TimeSliceViewType) => {
    setPulseView(newView);
  }

  return {
    pulseView,
    timeSliceWindow,
    startDate: timeSliceWindow.startTime,
    endDate: timeSliceWindow.endTime,
    handlePulseViewChange,
    handleEndDateChange,
    minSelectableDateString: selectedCustomer?.minDateAllowed || getDefaultMinDateString(),
    maxSelectableDateString: selectedCustomer?.maxDateAllowed || getTodayString(),
  };
};